import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";

const SECTIONS = [
  { key: "facilities",      label: "Facilities" },
  { key: "advantage",       label: "Advantage" },
  { key: "life_at_academy", label: "Life at Academy" },
  { key: "achievements",    label: "Achievements" },
  { key: "pathway",         label: "Pathway" },
  { key: "sessions",        label: "Sessions" },
];

export default function AdminAcademyContent() {
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("facilities");
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/academy-content"); setC(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async (section, data) => {
    setSaving(true);
    try {
      const { data: fresh } = await api.post("/admin/academy-content", { section, data });
      setC(fresh);
      toast.success("Section saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!c) return <div className="text-white/60">Loading…</div>;

  return (
    <div data-testid="admin-academy-cms">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Content · Academy Page</div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Academy Content.</h1>
        <a href="/academy" target="_blank" rel="noreferrer"
          data-testid="academy-view-live"
          className="border border-brand-orange text-brand-orange px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black">
          View Live Page ↗
        </a>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/12 mb-8 overflow-x-auto" data-testid="academy-cms-tabs">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`academy-tab-${s.key}`}
            className={`px-5 py-3 text-xs font-mono uppercase tracking-widest whitespace-nowrap ${tab === s.key ? "border-b-2 border-brand-orange brand-orange" : "text-white/50 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {tab === "facilities"      && <FacilitiesEditor data={c.facilities}      onSave={(d) => save("facilities", d)}      saving={saving} />}
      {tab === "advantage"       && <AdvantageEditor  data={c.advantage}       onSave={(d) => save("advantage",  d)}      saving={saving} />}
      {tab === "life_at_academy" && <LifeEditor       data={c.life_at_academy} onSave={(d) => save("life_at_academy", d)} saving={saving} />}
      {tab === "achievements"    && <AchievementsEditor data={c.achievements}  onSave={(d) => save("achievements", d)}    saving={saving} />}
      {tab === "pathway"         && <PathwayEditor    data={c.pathway}         onSave={(d) => save("pathway", d)}         saving={saving} />}
      {tab === "sessions"        && <SessionsEditor   data={c.sessions}        onSave={(d) => save("sessions", d)}        saving={saving} />}
    </div>
  );
}

/* -------- Small primitives -------- */
function TitleSubtitle({ form, setForm }) {
  return (
    <div className="space-y-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Section Title">
          <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="section-title" className="w-full bg-transparent border border-white/20 py-3 px-4" />
        </Field>
        <Field label="Section Subtitle">
          <input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            data-testid="section-subtitle" className="w-full bg-transparent border border-white/20 py-3 px-4" />
        </Field>
      </div>
      <Field label="Hero Image URL">
        <input value={form.hero_image || ""} onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
          data-testid="section-hero-image" placeholder="https://…" className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm" />
      </Field>
      <Field label="Intro Paragraph (shown below the title)">
        <textarea rows={3} value={form.intro || ""} onChange={(e) => setForm({ ...form, intro: e.target.value })}
          data-testid="section-intro" className="w-full bg-transparent border border-white/20 py-3 px-4 text-sm" />
      </Field>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}
function SaveBar({ onSave, saving, count }) {
  return (
    <div className="sticky bottom-4 flex items-center justify-between bg-brand-blue text-white px-5 py-3 mt-8 shadow-2xl">
      <div className="text-xs font-mono uppercase tracking-widest">
        {count !== undefined ? `${count} item${count === 1 ? "" : "s"} · ` : ""}Ready to save
      </div>
      <button onClick={onSave} disabled={saving} data-testid="section-save"
        className="bg-black text-white px-6 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/10 flex items-center gap-2 disabled:opacity-50">
        <Save size={13} /> {saving ? "Saving…" : "Save Section"}
      </button>
    </div>
  );
}
function MoveButtons({ arr, i, setArr }) {
  const move = (from, to) => {
    if (to < 0 || to >= arr.length) return;
    const cp = arr.slice();
    [cp[from], cp[to]] = [cp[to], cp[from]];
    setArr(cp);
  };
  return (
    <div className="flex flex-col">
      <button type="button" onClick={() => move(i, i - 1)} className="text-white/40 hover:text-white p-1"><ChevronUp size={14} /></button>
      <button type="button" onClick={() => move(i, i + 1)} className="text-white/40 hover:text-white p-1"><ChevronDown size={14} /></button>
    </div>
  );
}

/* -------- Facilities -------- */
function FacilitiesEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const items = form.items || [];
  const setItems = (n) => setForm({ ...form, items: n });
  const add = () => setItems([...items, { name: "", description: "", image_url: "", icon: "Trophy", bullets: [] }]);
  const update = (i, patch) => setItems(items.map((x, j) => j === i ? { ...x, ...patch } : x));
  const remove = (i) => setItems(items.filter((_, j) => j !== i));

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-5" data-testid={`facility-editor-${i}`}>
            <div className="flex items-start gap-3">
              <MoveButtons arr={items} i={i} setArr={setItems} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name"><input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Icon (Trophy, Target, Feather, Dumbbell, Goal, Cpu, BedDouble, Waves)"><input value={it.icon || ""} onChange={(e) => update(i, { icon: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 font-mono text-sm" /></Field>
                <div className="md:col-span-2">
                  <Field label="Description"><textarea rows={2} value={it.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Image">
                    <ImageUploader value={it.image_url || ""} onUploaded={(url) => update(i, { image_url: url })} onCleared={() => update(i, { image_url: "" })}
                      testid={`facility-img-${i}`} label={it.image_url ? "Replace" : "Upload"} />
                    <input value={it.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} placeholder="…or paste URL"
                      className="w-full bg-transparent border border-white/20 py-2 px-3 mt-2 text-xs font-mono" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <BulletsEditor value={it.bullets || []} onChange={(bullets) => update(i, { bullets })} testid={`facility-bullets-${i}`} />
                </div>
              </div>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        <button onClick={add} data-testid="add-facility" className="w-full border border-dashed border-white/20 py-4 text-white/60 hover:text-white hover:border-white/40 flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest">
          <Plus size={16} /> Add Facility
        </button>
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} count={items.length} />
    </div>
  );
}

/* -------- Bullets sub-editor -------- */
function BulletsEditor({ value, onChange, testid }) {
  const items = value || [];
  return (
    <Field label="Bullets (checklist)">
      <div className="space-y-1.5" data-testid={testid}>
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <span className="pt-2 text-white/40">•</span>
            <input value={v} onChange={(e) => onChange(items.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 bg-transparent border border-white/20 py-2 px-3 text-xs" />
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-2"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => onChange([...items, ""])} className="text-xs font-mono uppercase text-white/50 hover:text-white flex items-center gap-1 border border-dashed border-white/15 px-3 py-1.5">
          <Plus size={12} /> Add bullet
        </button>
      </div>
    </Field>
  );
}

/* -------- Advantage (same Block layout as Facilities) -------- */
function AdvantageEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const items = form.items || [];
  const setItems = (n) => setForm({ ...form, items: n });
  const add = () => setItems([...items, { name: "", description: "", image_url: "", icon: "Sparkles", bullets: [] }]);
  const update = (i, patch) => setItems(items.map((x, j) => j === i ? { ...x, ...patch } : x));
  const remove = (i) => setItems(items.filter((_, j) => j !== i));

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-5" data-testid={`advantage-editor-${i}`}>
            <div className="flex items-start gap-3">
              <MoveButtons arr={items} i={i} setArr={setItems} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Title"><input value={it.name || it.title || ""} onChange={(e) => update(i, { name: e.target.value, title: undefined })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Icon (Award, Cpu, Users, HeartHandshake, Trophy, GraduationCap, Sparkles)"><input value={it.icon || ""} onChange={(e) => update(i, { icon: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 font-mono text-sm" /></Field>
                <div className="md:col-span-2"><Field label="Description"><textarea rows={2} value={it.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
                <div className="md:col-span-2">
                  <Field label="Image">
                    <ImageUploader value={it.image_url || ""} onUploaded={(url) => update(i, { image_url: url })} onCleared={() => update(i, { image_url: "" })} testid={`adv-img-${i}`} label={it.image_url ? "Replace" : "Upload"} />
                    <input value={it.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} placeholder="…or paste URL" className="w-full bg-transparent border border-white/20 py-2 px-3 mt-2 text-xs font-mono" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <BulletsEditor value={it.bullets || []} onChange={(bullets) => update(i, { bullets })} testid={`adv-bullets-${i}`} />
                </div>
              </div>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        <button onClick={add} data-testid="add-advantage" className="w-full border border-dashed border-white/20 py-4 text-white/60 hover:text-white hover:border-white/40 flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest">
          <Plus size={16} /> Add Advantage
        </button>
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} count={items.length} />
    </div>
  );
}

/* -------- Life at Academy (items with block pattern) -------- */
function LifeEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const items = form.items || [];
  const setItems = (n) => setForm({ ...form, items: n });
  const add = () => setItems([...items, { name: "", description: "", image_url: "", icon: "MapPin", bullets: [] }]);
  const update = (i, patch) => setItems(items.map((x, j) => j === i ? { ...x, ...patch } : x));
  const remove = (i) => setItems(items.filter((_, j) => j !== i));

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-5" data-testid={`life-editor-${i}`}>
            <div className="flex items-start gap-3">
              <MoveButtons arr={items} i={i} setArr={setItems} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Block Title (e.g. '5:30 AM — Fitness First')"><input value={it.name || ""} onChange={(e) => update(i, { name: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Icon"><input value={it.icon || ""} onChange={(e) => update(i, { icon: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 font-mono text-sm" /></Field>
                <div className="md:col-span-2"><Field label="Description"><textarea rows={3} value={it.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
                <div className="md:col-span-2">
                  <Field label="Image">
                    <ImageUploader value={it.image_url || ""} onUploaded={(url) => update(i, { image_url: url })} onCleared={() => update(i, { image_url: "" })} testid={`life-img-${i}`} label={it.image_url ? "Replace" : "Upload"} />
                    <input value={it.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} placeholder="…or paste URL" className="w-full bg-transparent border border-white/20 py-2 px-3 mt-2 text-xs font-mono" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <BulletsEditor value={it.bullets || []} onChange={(bullets) => update(i, { bullets })} testid={`life-bullets-${i}`} />
                </div>
              </div>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        <button onClick={add} data-testid="add-life-block" className="w-full border border-dashed border-white/20 py-4 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest flex items-center justify-center gap-2">
          <Plus size={16} /> Add Block
        </button>
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} count={items.length} />
    </div>
  );
}

/* -------- Achievements -------- */
function AchievementsEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const items = form.items || [];
  const setItems = (n) => setForm({ ...form, items: n });
  const add = () => setItems([{ title: "", year: "", athlete_name: "", description: "", image_url: "", video_url: "" }, ...items]);
  const update = (i, patch) => setItems(items.map((x, j) => j === i ? { ...x, ...patch } : x));
  const remove = (i) => setItems(items.filter((_, j) => j !== i));

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />
      <button onClick={add} data-testid="add-achievement" className="mb-4 bg-brand-orange text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:glow-orange flex items-center gap-2">
        <Plus size={14} /> Add Achievement
      </button>
      <div className="space-y-4">
        {items.length === 0 && <div className="border border-dashed border-white/20 p-10 text-center text-white/40 text-sm">No achievements yet. Click <span className="brand-orange">Add Achievement</span> to record your first trophy or placement.</div>}
        {items.map((a, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-5" data-testid={`achievement-editor-${i}`}>
            <div className="flex items-start gap-3">
              <MoveButtons arr={items} i={i} setArr={setItems} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Title *"><input value={a.title} onChange={(e) => update(i, { title: e.target.value })} data-testid={`ach-title-${i}`} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Year"><input value={a.year || ""} onChange={(e) => update(i, { year: e.target.value })} data-testid={`ach-year-${i}`} placeholder="2025" className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Athlete Name"><input value={a.athlete_name || ""} onChange={(e) => update(i, { athlete_name: e.target.value })} data-testid={`ach-athlete-${i}`} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <div className="md:col-span-3"><Field label="Description"><textarea rows={2} value={a.description || ""} onChange={(e) => update(i, { description: e.target.value })} data-testid={`ach-desc-${i}`} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
                <div className="md:col-span-2">
                  <Field label="Photo">
                    <ImageUploader value={a.image_url || ""} onUploaded={(url) => update(i, { image_url: url })} onCleared={() => update(i, { image_url: "" })} testid={`ach-img-${i}`} label={a.image_url ? "Replace" : "Upload"} />
                    <input value={a.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} placeholder="…or paste URL" className="w-full bg-transparent border border-white/20 py-2 px-3 mt-2 text-xs font-mono" />
                  </Field>
                </div>
                <Field label="Video URL (YouTube / Vimeo)"><input value={a.video_url || ""} onChange={(e) => update(i, { video_url: e.target.value })} data-testid={`ach-video-${i}`} placeholder="https://youtu.be/…" className="w-full bg-transparent border border-white/20 py-2 px-3 text-xs font-mono" /></Field>
              </div>
              <button onClick={() => remove(i)} data-testid={`ach-del-${i}`} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} count={items.length} />
    </div>
  );
}

/* -------- Pathway -------- */
function PathwayEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const stages = form.stages || [];
  const setStages = (n) => setForm({ ...form, stages: n });
  const add = () => setStages([...stages, { level: `0${stages.length + 1}`, title: "", age_group: "", duration: "", description: "", left_label: "", right_label: "" }]);
  const update = (i, patch) => setStages(stages.map((x, j) => j === i ? { ...x, ...patch } : x));
  const remove = (i) => setStages(stages.filter((_, j) => j !== i));

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />
      <p className="text-xs text-white/50 -mt-2 mb-4">Ordered <b>bottom → top</b> (beginners at level 01, pro at highest). The pyramid renders widest at the bottom automatically.</p>
      <div className="space-y-3">
        {stages.map((s, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-4" data-testid={`pathway-editor-${i}`}>
            <div className="flex items-start gap-3">
              <MoveButtons arr={stages} i={i} setArr={setStages} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                <Field label="Level"><input value={s.level || ""} onChange={(e) => update(i, { level: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 font-mono" /></Field>
                <Field label="Stage Title"><input value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Age Group"><input value={s.age_group || ""} onChange={(e) => update(i, { age_group: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <Field label="Duration"><input value={s.duration || ""} onChange={(e) => update(i, { duration: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3" /></Field>
                <div className="md:col-span-2"><Field label="Left Label (competition side)"><input value={s.left_label || ""} onChange={(e) => update(i, { left_label: e.target.value })} placeholder="e.g. Fun Game" className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
                <div className="md:col-span-2"><Field label="Right Label (performance side)"><input value={s.right_label || ""} onChange={(e) => update(i, { right_label: e.target.value })} placeholder="e.g. Introducing Techniques" className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
                <div className="md:col-span-4"><Field label="Description (for expanded view)"><textarea rows={2} value={s.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" /></Field></div>
              </div>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        <button onClick={add} data-testid="add-pathway" className="w-full border border-dashed border-white/20 py-3 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest flex items-center justify-center gap-2">
          <Plus size={14} /> Add Stage
        </button>
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} count={stages.length} />
    </div>
  );
}

/* -------- Sessions (curriculum tree + weekly schedule) -------- */
function SessionsEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const curr = form.curriculum || { one_on_one: { title: "One on One", sub_branches: [] }, group_classes: { title: "Group Classes", items: [] }, matches: { title: "Matches", items: [] } };
  const setCurr = (c) => setForm({ ...form, curriculum: c });
  const rows = form.schedule || [];
  const setRows = (n) => setForm({ ...form, schedule: n });

  const oneOnOne = curr.one_on_one || { title: "One on One", sub_branches: [] };
  const setOne = (patch) => setCurr({ ...curr, one_on_one: { ...oneOnOne, ...patch } });
  const subBranches = oneOnOne.sub_branches || [];

  const group = curr.group_classes || { title: "Group Classes", items: [] };
  const matches = curr.matches || { title: "Matches", items: [] };

  const setStrList = (branchKey, target, items) => {
    if (branchKey === "group") setCurr({ ...curr, group_classes: { ...group, items } });
    if (branchKey === "matches") setCurr({ ...curr, matches: { ...matches, items } });
  };

  return (
    <div>
      <TitleSubtitle form={form} setForm={setForm} />

      <Field label="Note (shown above the weekly schedule)">
        <input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="w-full bg-transparent border border-white/20 py-3 px-4 mb-6" />
      </Field>

      {/* Curriculum tree editor */}
      <div className="border border-white/12 bg-[#131A2B] p-5 mb-8" data-testid="sessions-curriculum">
        <div className="font-display text-lg uppercase font-bold brand-orange mb-4">Curriculum Tree (shown as diagram on Sessions page)</div>

        {/* One-on-One with sub-branches */}
        <div className="mb-8 pb-8 border-b border-white/10">
          <input value={oneOnOne.title || ""} onChange={(e) => setOne({ title: e.target.value })}
            className="w-full bg-brand-blue text-white text-center py-2 px-4 font-display font-black uppercase tracking-tight mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {subBranches.map((sb, i) => (
              <div key={i} className="border border-white/12 p-3" data-testid={`one-on-one-sub-${i}`}>
                <div className="flex gap-2 mb-3">
                  <input value={sb.name || ""} onChange={(e) => setOne({ sub_branches: subBranches.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })}
                    placeholder="Branch name (e.g. Batting)" className="flex-1 bg-brand-blue text-white text-center py-1.5 px-3 font-bold uppercase text-xs" />
                  <button onClick={() => setOne({ sub_branches: subBranches.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                </div>
                <SimpleStringList value={sb.items || []} onChange={(items) => setOne({ sub_branches: subBranches.map((x, j) => j === i ? { ...x, items } : x) })}
                  testid={`one-on-one-items-${i}`} placeholder="Skill item" />
              </div>
            ))}
            <button onClick={() => setOne({ sub_branches: [...subBranches, { name: "", items: [] }] })}
              data-testid="add-one-on-one-sub"
              className="border border-dashed border-white/20 py-6 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white flex items-center justify-center gap-2">
              <Plus size={14} /> Add branch
            </button>
          </div>
        </div>

        {/* Group Classes */}
        <div className="mb-8 pb-8 border-b border-white/10">
          <input value={group.title || ""} onChange={(e) => setCurr({ ...curr, group_classes: { ...group, title: e.target.value } })}
            className="w-full bg-brand-blue text-white text-center py-2 px-4 font-display font-black uppercase tracking-tight mb-4" />
          <SimpleStringList value={group.items || []} onChange={(items) => setStrList("group", null, items)} testid="group-classes-items" placeholder="Class topic" />
        </div>

        {/* Matches */}
        <div>
          <input value={matches.title || ""} onChange={(e) => setCurr({ ...curr, matches: { ...matches, title: e.target.value } })}
            className="w-full bg-brand-blue text-white text-center py-2 px-4 font-display font-black uppercase tracking-tight mb-4" />
          <SimpleStringList value={matches.items || []} onChange={(items) => setStrList("matches", null, items)} testid="matches-items" placeholder="Match topic" />
        </div>
      </div>

      {/* Weekly schedule */}
      <div className="font-display text-lg uppercase font-bold brand-blue mb-3">Weekly Schedule (day-columns table)</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="bg-[#131A2B] border border-white/12 p-3 flex items-center gap-2" data-testid={`session-row-${i}`}>
            <MoveButtons arr={rows} i={i} setArr={setRows} />
            <select value={r.day} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, day: e.target.value } : x))} className="bg-[#0B1120] border border-white/20 py-2 px-3 font-mono uppercase text-xs">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <option key={d}>{d}</option>)}
            </select>
            <input value={r.time || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, time: e.target.value } : x))} placeholder="05:30-07:00" className="w-32 bg-transparent border border-white/20 py-2 px-3 font-mono text-xs" />
            <input value={r.sport || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, sport: e.target.value } : x))} placeholder="Sport · Batch" className="flex-1 bg-transparent border border-white/20 py-2 px-3 text-sm" />
            <input value={r.coach || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, coach: e.target.value } : x))} placeholder="Coach" className="w-40 bg-transparent border border-white/20 py-2 px-3 text-sm" />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => setRows([...rows, { day: "Mon", time: "", sport: "", coach: "" }])} data-testid="add-session" className="w-full border border-dashed border-white/20 py-3 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest flex items-center justify-center gap-2">
          <Plus size={14} /> Add Session Row
        </button>
      </div>
      <SaveBar onSave={() => onSave(form)} saving={saving} />
    </div>
  );
}

function SimpleStringList({ value, onChange, testid, placeholder }) {
  const items = value || [];
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input value={v} onChange={(e) => onChange(items.map((x, j) => j === i ? e.target.value : x))}
            placeholder={placeholder}
            className="flex-1 bg-brand-orange text-white text-center py-1.5 px-3 text-xs font-medium border border-transparent focus:outline-none focus:border-white" />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-1"><Trash2 size={13} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...items, ""])} className="w-full border border-dashed border-white/15 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white flex items-center justify-center gap-1">
        <Plus size={11} /> Add
      </button>
    </div>
  );
}
